import {
  Dropdown,
  DropdownGroup,
  DropdownItem,
  IconButton,
  isCurrency,
  openDialog,
  Tooltip,
} from 'platform/components';
import {HStack, Icon, Show, Spinner, Text, themeIcons, ThemeIconKey} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import styled from 'styled-components';

import {useState} from 'react';

import {useSourcingAllowedCountryAndCurrencyQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useSetUserSettingsItemMutation} from '@dms/api/customer';
import {PricingStrategy} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getPricingStrategyOptions} from '../../../constants/pricingStrategyOptions';
import {useApiEnums} from '../../../hooks/useApiEnums';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {useSourcingType} from '../../../hooks/useSourcingType';
import {SetPricingStrategyDialog} from './SetPricingStrategyDialog';

type PendingSetting = 'country' | 'currency' | 'pricing';

function isThemeIconKey(value: string): value is ThemeIconKey {
  return value in themeIcons;
}

function getCountryFlagIcon(countryCode: string | Nullish): ThemeIconKey {
  const flagKey = `flags/${countryCode ?? ''}`;
  return isThemeIconKey(flagKey) ? flagKey : 'fallback';
}

const PRICING_STRATEGY_ICONS: Record<PricingStrategy, ThemeIconKey> = {
  [PricingStrategy.FAIR_PRICE]: 'custom/ai_fair_market_price',
  [PricingStrategy.LFPP]: 'custom/first_page_price',
};

export function HeaderSettings(props: TestIdProps) {
  const [updateUserSettings, {isLoading: isMutationLoading}] = useSetUserSettingsItemMutation();
  const {isFetching: isCurrentUserFetching} = useGetCurrentUserInfoQuery();
  const {data} = useSourcingAllowedCountryAndCurrencyQuery();
  const {getEnum} = useApiEnums();
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const country = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const pricingStrategy = useSourcingPricingStrategy();
  const sourcingType = useSourcingType();

  const [lastPendingSetting, setLastPendingSetting] = useState<PendingSetting | null>(null);

  const isSettingPending = isMutationLoading || isCurrentUserFetching;
  const isCountryPending = lastPendingSetting === 'country' && isSettingPending;
  const isCurrencyPending = lastPendingSetting === 'currency' && isSettingPending;
  const isPricingPending = lastPendingSetting === 'pricing' && isSettingPending;

  const countryLabels = getEnum('country');
  const getCountryLabel = (code: string) =>
    countryLabels.find(({key}) => key === code)?.label ?? code;
  const getCurrencyLabel = (code: string) =>
    isCurrency(code) ? formatCurrencySymbol(code) || code : code;

  const currencySymbol = currency?.code ? getCurrencyLabel(currency.code) : '';

  const persistSetting = (settingKey: string, value: string, pending: PendingSetting) => {
    setLastPendingSetting(pending);
    updateUserSettings({
      settingKey,
      userSettingRequestBody: {value},
    })
      .unwrap()
      .catch(handleApiError)
      .finally(() => {
        setLastPendingSetting(null);
      });
  };

  const onChangeCountry = (value: string) => {
    if (value === country?.code) {
      return;
    }
    persistSetting('sourcing_buyer_country', value, 'country');
  };

  const onChangeCurrency = (value: string) => {
    if (value === currency?.code) {
      return;
    }
    persistSetting('sourcing_currency', value, 'currency');
  };

  const onApplyPricingStrategy = (value: PricingStrategy) => {
    if (value === pricingStrategy) {
      return;
    }
    persistSetting('sourcing_pricing_strategy', value, 'pricing');
  };

  const openPricingStrategyDialog = () => {
    openDialog(
      <SetPricingStrategyDialog
        initialStrategy={pricingStrategy}
        onApply={onApplyPricingStrategy}
        options={getPricingStrategyOptions(sourcingType)}
        data-testid={suffixTestId('pricingStrategyDialog', props)}
      />,
      {
        title: i18n.t('page.sourcing.labels.pricingStrategy.setDialogTitle'),
        size: 'default',
        'data-testid': testIds.sourcing.classifieds('headerSettings-pricingStrategyDialog'),
      }
    );
  };

  return (
    <HStack align="center">
      <Dropdown
        isDisabled={isCountryPending}
        dropdownControl={
          <Tooltip label={i18n.t('entity.address.labels.targetCountry')}>
            <IconButton
              icon={getCountryFlagIcon(country?.code)}
              isLoading={isCountryPending}
              aria-label={i18n.t('entity.address.labels.targetCountry')}
              data-testid={suffixTestId('targetCountryTrigger', props)}
            />
          </Tooltip>
        }
        data-testid={testIds.sourcing.classifieds('headerSettings-targetCountry')}
      >
        <DropdownGroup label={i18n.t('entity.address.labels.targetCountry')}>
          {data?.countries?.map((countryCode) => (
            <DropdownItem
              key={countryCode}
              leftIcon={getCountryFlagIcon(countryCode)}
              label={getCountryLabel(countryCode)}
              isActive={countryCode === country?.code}
              suffix={
                countryCode === country?.code ? (
                  <Icon value="navigation/check" color="general.accent" size={4} />
                ) : undefined
              }
              onClick={() => onChangeCountry(countryCode)}
            />
          ))}
        </DropdownGroup>
      </Dropdown>

      <Dropdown
        isDisabled={isCurrencyPending}
        dropdownControl={
          <Tooltip label={i18n.t('entity.bank.labels.currency')}>
            <CurrencyTriggerButton
              type="button"
              $isLoading={isCurrencyPending}
              aria-label={i18n.t('entity.bank.labels.currency')}
              data-testid={suffixTestId('currencyTrigger', props)}
            >
              <Show when={isCurrencyPending}>
                <Spinner
                  variant="overlay"
                  size="small"
                  data-testid={suffixTestId('currencyTriggerLoader', props)}
                />
              </Show>
              <Text size="small" alternative>
                {currencySymbol}
              </Text>
            </CurrencyTriggerButton>
          </Tooltip>
        }
        data-testid={testIds.sourcing.classifieds('headerSettings-currency')}
      >
        <DropdownGroup label={i18n.t('entity.bank.labels.currency')}>
          {data?.currencies?.map((code) => (
            <DropdownItem
              key={code}
              label={getCurrencyLabel(code)}
              isActive={code === currency?.code}
              suffix={
                code === currency?.code ? (
                  <Icon value="navigation/check" color="general.accent" size={4} />
                ) : undefined
              }
              onClick={() => onChangeCurrency(code)}
            />
          ))}
        </DropdownGroup>
      </Dropdown>

      <Tooltip label={i18n.t('page.sourcing.labels.pricingStrategy')}>
        <IconButton
          icon={PRICING_STRATEGY_ICONS[pricingStrategy]}
          isLoading={isPricingPending}
          onClick={openPricingStrategyDialog}
          aria-label={i18n.t('page.sourcing.labels.pricingStrategy')}
          data-testid={testIds.sourcing.classifieds('headerSettings-pricingStrategy')}
        />
      </Tooltip>
    </HStack>
  );
}

/** Matches IconButton default size/hover; fixed width so Kč/€/zł don't shift layout. */
const CurrencyTriggerButton = styled.button<{$isLoading?: boolean}>`
  position: relative;
  width: ${({theme}) => theme.getSize(10)};
  height: ${({theme}) => theme.getSize(8)};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: none;
  border-radius: ${({theme}) => theme.radii.small};
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  pointer-events: ${({$isLoading}) => ($isLoading ? 'none' : 'all')};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }

  &:active {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][100]};
  }
`;
